export const dashboards = {
  title: "Dashboards",
  subtitle: "Live dashboards built from your data. Every widget keeps refreshing on its own.",
  newDashboard: "New dashboard",
  loading: "Loading dashboard",
  empty: {
    title: "No dashboards yet",
    hint: "Start from a template or an empty canvas.",
    widgets: "This dashboard is empty",
    widgetsHint: "Add a widget from the gallery or build your own from any dataset.",
  },
  calm: {
    yours: "Your dashboards",
    accounts: "Account dashboards",
    accountsHint: "One live dashboard per connected Instagram account.",
    accountNew: "Ready to open",
    seeAnalytics: "See Instagram analytics",
    emptyHint: "Build a dashboard from templates, or start with Instagram analytics.",
  },
  list: {
    widgets_one: "Widgets: {{count}}",
    widgets_other: "Widgets: {{count}}",
    team: "Team",
    private: "Private",
    default: "Default",
    open: "Open",
    delete: "Delete dashboard",
    deleteConfirm: "Delete “{{name}}”?",
    deleteHint: "Its widgets are removed for everyone it's shared with.",
    deleted: "Dashboard deleted",
    updated: "Updated {{ago}}",
    scopes: { org: "Organization", account: "Account", post: "Post", campaign: "Campaign" },
  },
  create: {
    title: "New dashboard",
    name: "Name",
    namePlaceholder: "e.g. Ramadan campaign",
    template: "Start from",
    blank: "Empty canvas",
    blankHint: "Add widgets one by one.",
    shared: "Share with the team",
    sharedHint: "Teammates can view it; editors can change it.",
    create: "Create dashboard",
    created: "Dashboard created",
  },
  templates: {
    home: "Home",
    homeHint: "Reach, comments, sentiment, automation and top posts.",
    account: "Account",
    accountHint: "One account's audience, activity and content.",
    post: "Post",
    postHint: "One post's performance, comments and automation.",
    campaign: "Campaign",
    campaignHint: "A group of posts compared side by side.",
  },
  toolbar: {
    edit: "Edit dashboard",
    done: "Done",
    add: "Add widget",
    reset: "Reset to template",
    resetConfirm: "Reset this dashboard?",
    resetHint: "Widgets and filters go back to the template. Your changes are lost.",
    resetDone: "Dashboard reset",
    shareTeam: "Share with the team",
    sharePrivate: "Make private",
    shared: "Shared with the team",
    privateNow: "Only you can see it",
    makeDefault: "Set as default",
    defaultSet: "This is now the default dashboard",
    rename: "Rename dashboard",
    more: "Dashboard options",
    viewOnly: "View only",
    editing: "Editing: drag widgets by their title, resize from the corner.",
    allDashboards: "All dashboards",
  },
  filters: {
    label: "Dashboard filters",
    time: "Time range",
    compare: "Compare",
    compareHint: "Compare to the previous period",
    compareAllTime: "All time has no previous period",
    accounts: "Accounts",
    channels: "Channels",
    sentiment: "Sentiment",
    intent: "Intent",
    posts: "Posts",
    searchPosts: "Search posts",
    noPosts: "No posts match",
    any: "Any",
    clear: "Clear filters",
    selected_one: "{{count}} selected",
    selected_other: "{{count}} selected",
    removeFilter: "Remove filter {{label}}",
    from: "From",
    to: "To",
    apply: "Apply",
    tags: "Tags",
    searchTags: "Search tags",
    noTags: "No tags match",
    category: "AI category",
    builtIn: "Built-in tag",
    customTag: "Your tag",
    presets: {
      today: "Today",
      "24h": "Last 24 hours",
      "7d": "Last 7 days",
      "30d": "Last 30 days",
      "90d": "Last 90 days",
      mtd: "Month to date",
      ytd: "Year to date",
      all: "All time",
      custom: "Custom range",
    },
  },
  widget: {
    updated: "Updated {{ago}}",
    updatedNow: "Updated just now",
    live: "Live",
    refreshing: "Refreshing",
    refresh: "Refresh",
    menu: "Options for {{title}}",
    edit: "Edit",
    duplicate: "Duplicate",
    duplicated: "Widget duplicated",
    fullscreen: "Full screen",
    exportPng: "Export as PNG",
    exportCsv: "Export as CSV",
    delete: "Remove",
    deleted: "Removed “{{title}}”",
    undo: "Undo",
    restored: "Widget restored",
    noData: "No data in this range",
    error: "Couldn't load this widget",
    retry: "Retry",
    ignored: "Not filtered by {{list}}",
    vsPrevious: "vs previous period",
    noPrevious: "No previous data",
    titleLabel: "Widget title",
    drill: "Click a point to see its items",
    grab: "Move or resize {{title}}",
    keyboardHint: "Arrow keys move the widget, Shift + arrows resize it, Escape stops.",
    moved: "Moved to column {{x}}, row {{y}}",
    resized: "Resized to {{w}} by {{h}}",
    other: "Other",
    truncated_one: "Showing the first {{count}} row",
    truncated_other: "Showing the first {{count}} rows",
    total: "Total",
    goal: "Goal {{value}}",
    open: "Open",
    exportFailed: "Export failed",
  },
  editor: {
    addTitle: "Add a widget",
    editTitle: "Edit widget",
    gallery: "Gallery",
    custom: "Build your own",
    dataset: "Data",
    measures: "Measures",
    addMeasure: "Add measure",
    groupBy: "Group by",
    addGroup: "Add grouping",
    filters: "Filters",
    addFilter: "Add filter",
    viz: "Visualization",
    options: "Options",
    preview: "Live preview",
    title: "Title",
    subtitle: "Subtitle (optional)",
    fn: "Function",
    field: "Field",
    alias: "Column name",
    granularity: "Bucket",
    op: "Condition",
    value: "Value",
    where: "Only rows where",
    format: "Number format",
    legend: "Show legend",
    stacked: "Stack series",
    showCompare: "Show previous period",
    topN: "Rows to show",
    color: "Color",
    text: "Text (Markdown)",
    refresh: "Refresh",
    inherit: "Use dashboard filters",
    inheritHint: "The time range and filters from the bar above apply to this widget.",
    save: "Save widget",
    add: "Add to dashboard",
    added: "Widget added",
    saved: "Widget saved",
    cancel: "Cancel",
    remove: "Remove",
    records: "Items (no measures): lists and tables of individual items",
    noMeasure: "No measures: the widget lists individual items.",
    allFields: "All fields",
    pickTemplate: "Pick a starting point",
    yes: "Yes",
    no: "No",
    fns: { count: "Count", sum: "Sum", avg: "Average", min: "Minimum", max: "Maximum", p95: "95th percentile", rate: "Share of rows", distinct: "Distinct count" },
    ops: { eq: "is", neq: "is not", in: "is any of", nin: "is none of", gt: "is more than", gte: "is at least", lt: "is less than", lte: "is at most", contains: "contains", semantic: "is about" },
    granularities: { hour: "Hour", day: "Day", week: "Week", month: "Month", hour_of_day: "Hour of day", day_of_week: "Day of week" },
    refreshModes: { live: "Live (as data changes)", "30s": "Every 30 seconds", "1m": "Every minute", "5m": "Every 5 minutes", manual: "Manually" },
    formats: { number: "Number", compact: "Compact (12.4K)", percent: "Percent", currency: "Currency", duration: "Duration" },
    categories: { all: "All", engagement: "Engagement", audience: "Audience", inbox: "Inbox", automation: "Automation", content: "Content", operations: "Operations" },
  },
  actions: {
    segmentLabel: "Actions for {{label}}",
    createRule: "Create rule from this",
    apply: "Apply action to these items",
    closeSegment: "Close the actions",
    ruleTitle: "Create a rule from this",
    ruleHint: "A rule that acts on new items like “{{label}}”. Review it in the rule builder before you turn it on.",
    cancel: "Cancel",
    openBuilder: "Open in rule builder",
    mapped: "Becomes the rule's conditions",
    nothingMapped: "No filter could become a condition: the rule matches every new item until you add conditions.",
    unmapped: "Not included",
    unmappedHint: "Rules act on each new comment or message as it arrives, so time ranges, statuses and numbers can't be part of a rule.",
    ruleInactive: "The rule opens switched off and in “approve” mode, with a tag action you can change.",
    applyTitle: "Apply an action to these items",
    review: "Review",
    itemsLabel: "Matching items",
    count: "{{what}} match",
    countCapped: "{{what}} match · the action applies to the newest {{n}}",
    comments: "{{n}} comments",
    conversations: "{{n}} conversations",
    pick: "What should happen?",
    kinds: { flag: "Tag", hide: "Hide", unhide: "Unhide", archive: "Archive", unarchive: "Unarchive", reply_with_agent: "Reply with an agent", assign: "Assign" },
    hints: {
      flag: "Add or remove tags and AI categories.",
      hide: "Hide the comments on Instagram (reversible).",
      unhide: "Show hidden comments again.",
      archive: "Move them out of the inbox (reversible).",
      unarchive: "Bring archived comments back.",
      reply_with_agent: "An AI agent drafts a reply for each; drafts wait for approval.",
      assign: "Give them to a teammate.",
    },
    titles: {
      flag: "Tag {{what}}",
      unflag: "Remove tags from {{what}}",
      hide: "Hide {{what}}",
      unhide: "Unhide {{what}}",
      archive: "Archive {{what}}",
      unarchive: "Unarchive {{what}}",
      reply_with_agent: "Draft replies to {{what}} with {{agent}}",
      assign: "Assign {{what}} to {{name}}",
    },
    addTags: "Add",
    removeTags: "Remove",
    pickAgent: "Choose an agent",
    agentHint: "Each draft is a pending approval; nothing is sent until someone approves it.",
    pickMember: "Choose a teammate",
  },
  viz: { kpi: "KPI", line: "Line", area: "Area", bar: "Bar", stacked_bar: "Stacked bar", donut: "Donut", heatmap: "Heatmap", table: "Table", funnel: "Funnel", comments: "Comment list", posts: "Post grid", text: "Text" },
  drill: {
    title: "Items behind “{{label}}”",
    hint: "The items this point is made of. Open one to act on it.",
    open: "Open",
    empty: "No items",
  },
  scoped: {
    accountTitle: "{{account}} dashboard",
    accountHint: "Live charts for this account only.",
    postTitle: "Post dashboard",
    postHint: "Live charts for this post. Switch to edit mode to add, remove or resize widgets.",
    openAccount: "Account dashboard",
    openPost: "Open dashboard",
    notFound: "This account isn't connected anymore",
  },
  datasets: {
    comments: "Comments",
    messages: "Direct messages",
    conversations: "Conversations",
    posts: "Posts",
    post_insights: "Post insights",
    post_metrics: "Post metrics over time",
    account_insights: "Account insights",
    automation_runs: "Automation runs",
    ai_usage: "AI usage",
    jobs: "Jobs",
  },
  fields: {
    comments: {
      id: "Comment", text: "Text", author: "Author", contact_id: "Contact", account_id: "Account", post_id: "Post", publication_id: "Publication", channel: "Channel", status: "Status",
      inbox_state: "Inbox state", sentiment: "Sentiment", intent: "Intent", language: "Language", toxicity: "Toxicity", confidence: "AI confidence", replies_count: "Replies",
      replied: "Answered by the account", response_minutes: "Response time (min)", is_reply: "Is a reply", by_account: "Written by the account", assigned: "Assigned", assignee_id: "Assignee", created_at: "Created",
      tag: "Tag", tagged_by_rule: "Tagged by rule",
    },
    messages: { id: "Message", conversation_id: "Conversation", account_id: "Account", channel: "Channel", direction: "Direction", sent_by: "Sent by", status: "Status", has_attachment: "Has attachment", text: "Text", tag: "Conversation tag", sent_at: "Sent" },
    conversations: {
      id: "Conversation", account_id: "Account", contact_id: "Contact", channel: "Channel", inbox_state: "Inbox state", sentiment: "Sentiment", intent: "Intent", unread: "Unread",
      ai_agent: "AI agent on", window_open: "24h window open", assigned: "Assigned", assignee_id: "Assignee", last_message_at: "Last message",
      tag: "Tag", tagged_by_rule: "Tagged by rule",
    },
    posts: {
      id: "Publication", post_id: "Post", account_id: "Account", channel: "Channel", type: "Type", status: "Status", caption: "Caption", permalink: "Link", media_url: "Media",
      imported: "Imported", comments_count: "Comments", likes_count: "Likes", published_at: "Published", scheduled_at: "Scheduled",
    },
    post_insights: {
      post_id: "Post", publication_id: "Publication", account_id: "Account", channel: "Channel", type: "Type", reach: "Reach", views: "Views", saves: "Saves", shares: "Shares",
      likes: "Likes", comments: "Comments", interactions: "Interactions", engagement_rate: "Engagement rate", published_at: "Published",
    },
    post_metrics: { post_id: "Post", publication_id: "Publication", account_id: "Account", reach: "Reach", views: "Views", likes: "Likes", saves: "Saves", shares: "Shares", comments: "Comments", day: "Day" },
    account_insights: {
      account_id: "Account", reach: "Reach", views: "Views", accounts_engaged: "Accounts engaged", total_interactions: "Interactions", likes: "Likes", comments: "Comments",
      shares: "Shares", saves: "Saves", profile_views: "Profile views", followers: "Followers", day: "Day",
    },
    automation_runs: { id: "Run", rule_id: "Rule", account_id: "Account", post_id: "Post", comment_id: "Comment", matched: "Matched", action: "Action", result: "Result", sentiment: "Sentiment", intent: "Intent", created_at: "Created" },
    ai_usage: { kind: "Kind", model: "Model", feature: "Feature", qty: "Quantity", at: "At" },
    jobs: { id: "Job", kind: "Kind", status: "Status", done: "Done", total: "Total", failed: "Failed", duration_sec: "Duration (s)", created_by: "Started by", created_at: "Created", finished_at: "Finished" },
  },
  measureNames: {
    comments: "Comments", negative_rate: "Negative share", reply_rate: "Reply rate", response_minutes: "Response time", handled: "Handled", evaluated: "Evaluated", matched: "Matched",
    executed: "Executed", runs: "Runs", messages: "Messages", open: "Open", jobs: "Jobs", tokens: "Tokens", count_all: "Count", reach: "Reach", views: "Views", likes: "Likes",
    saves: "Saves", followers: "Followers", engagement_rate: "Engagement rate",
  },
  values: {
    status: { visible: "Visible", hidden: "Hidden", deleted: "Deleted", received: "Received", sending: "Sending", sent: "Sent", delivered: "Delivered", read: "Read", failed: "Failed", draft: "Draft", scheduled: "Scheduled", publishing: "Publishing", published: "Published", cancelled: "Cancelled", queued: "Queued", running: "Running", paused: "Paused", succeeded: "Succeeded" },
    direction: { in: "Received", out: "Sent" },
    sent_by: { contact: "Contact", user: "Team", automation: "Automation", ai: "AI" },
    result: { executed: "Executed", pending_approval: "Waiting for approval", skipped: "Skipped", failed: "Failed", suggested: "Suggested" },
  },
  wt: {
    kpi_post_reach: { title: "Post reach", description: "Lifetime reach of the posts in range." },
    kpi_reach: { title: "Reach", description: "Accounts reached (daily account insights)." },
    kpi_comments: { title: "Comments", description: "Comments received, vs the previous period." },
    kpi_negative_rate: { title: "Negative share", description: "Share of comments with negative sentiment." },
    kpi_automation: { title: "Handled by automation", description: "Rule matches that ran an action." },
    comments_over_time: { title: "Comments over time", description: "Daily comments by sentiment." },
    sentiment_donut: { title: "Sentiment", description: "Comment sentiment split. Click a slice to filter the dashboard." },
    intent_bar: { title: "What people ask", description: "Comments by AI intent." },
    reach_trend: { title: "Reach & views", description: "Daily reach and views with the previous period." },
    activity_heatmap: { title: "When people comment", description: "Comments by weekday and hour." },
    latest_comments: { title: "Latest comments", description: "Newest comments, live." },
    negative_comments: { title: "Negative comments", description: "Comments that need care." },
    top_posts: { title: "Top posts", description: "Posts ranked by reach." },
    post_metrics_trend: { title: "Post performance over time", description: "Daily reach, views, likes, saves and comments." },
    top_commenters: { title: "Top commenters", description: "Who comments the most." },
    kpi_reply_rate: { title: "Reply rate", description: "Share of comments the account answered." },
    kpi_response_time: { title: "Response time", description: "Average minutes until the account replied." },
    post_table: { title: "Post performance", description: "Reach, views, saves and engagement per post." },
    automation_funnel: { title: "Automation funnel", description: "Events → matched → executed." },
    automation_by_rule: { title: "Actions by rule", description: "Which rules do the work." },
    dm_volume: { title: "DMs in vs out", description: "Messages received and sent per day." },
    open_conversations: { title: "Open conversations", description: "DM threads waiting on you." },
    followers_trend: { title: "Followers", description: "Follower count over time." },
    jobs_status: { title: "Jobs", description: "Long operations by status." },
    ai_usage_trend: { title: "AI usage", description: "AI tokens per day." },
    comments_by_tag: { title: "Comments by tag", description: "Comments per tag and AI category. Click a bar to filter." },
    tag_trend: { title: "Tag trend over time", description: "Tagged comments per day, by tag." },
    top_posts_by_tag: { title: "Top posts by tag", description: "Which posts collect which tags." },
    tagged_by_rule: { title: "Tagged by rule", description: "Comments each rule tagged." },
    note: { title: "Note", description: "A text block for context or goals." },
  },
};
